// services/api.ts
import { coreApiClient } from './api/core';
import { authApi } from './api/auth';
import { userApi } from './api/user';
import { setupApi } from './api/setup';
import { vocabApi } from './api/vocab';
import { notificationsApi } from './api/notifications';
import { guideApi } from './api/guide';
import { practiceApi } from './api/practice';
import { aiTutorApi } from './api/aiTutor';

// Re-export everything
export * from './api/core';
export * from './api/auth';
export * from './api/user';
export * from './api/vocab';
export * from './api/notifications';
export * from './api/guide';
export * from './api/practice';
export * from './api/aiTutor';


// Combine into a single object for backward compatibility if needed
// or just export the singleton as apiClient with all methods attached
// However, since we split them, it's better to use them separately.
// But to maintain backward compatibility with `apiClient.method()` calls,
// we can create a proxy or an object that combines them.

export const apiClient = {
    ...coreApiClient,
    ...authApi,
    ...userApi,
    ...setupApi,
    ...vocabApi,
    ...notificationsApi,
    ...guideApi,
    // We need to manually map the methods that were on the class instance
    // to the new services if we want to keep the exact same usage `apiClient.getWords(...)`
    // The spread above might not work for class methods if they are not enumerable or if we need `this` context.
    // But `authApi` etc are objects with functions, so spreading them works.
    // `coreApiClient` is a class instance. Spreading it might not copy methods from prototype.

    // Let's explicitly map the core methods if needed, or just rely on the fact that
    // most usage was `apiClient.getWords` etc.

    // Payment
    createOneTimePaymentSession: async () => {
        try {
            // Only for Hallouminati for now, hardcoded price ID handling on backend usually
            const response = await coreApiClient.axios.post<{ checkoutUrl: string }>('/api/payments/create-session', {
                product: 'hallouminati_guide_lifetime',
                price: 2000, // in cents, though backend should validate this
                currency: 'eur'
            });

            // Return structured response as expected by callers checking .success
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to initiate payment'
            };
        }
    },

    getPaywallData: async () => {
        try {
            const response = await coreApiClient.axios.get('/api/payments/paywall-data');
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch paywall data'
            };
        }
    },

    // Let's just export the combined object.
    // Core methods...
    getToken: coreApiClient.getToken.bind(coreApiClient),
    setTokenFromOAuth: coreApiClient.setTokenFromOAuth.bind(coreApiClient),
    ensureInitialized: coreApiClient.ensureInitialized.bind(coreApiClient),
    clearToken: coreApiClient.clearToken.bind(coreApiClient),
    loadToken: coreApiClient.loadToken.bind(coreApiClient),
    setAuthHandler: coreApiClient.setAuthHandler.bind(coreApiClient),
};

// Also export the individual services for cleaner imports in the future
export { coreApiClient, authApi, userApi, setupApi, vocabApi, notificationsApi, practiceApi, aiTutorApi };